import { Navigate, useRoutes } from 'react-router-dom';
// layouts
import AdminLayout from './layouts/admin';
import ProjectLayout from './layouts/project';
import MeasureLayout from './layouts/measure';
import SimpleLayout from './layouts/simple';
// auth
import { AuthGuard } from './components/auth-guard';
//
import DevicesPage from './pages/DevicesPage';
import ReadsPage from './pages/ReadsPage';
import SystemStatusPage from './pages/SystemStatusPage';
import FleetManagementPage from './pages/FleetManagementPage';
import LoginPage from './pages/LoginPage';
import WaitingPage from './pages/WaitingPage';
import Page404 from './pages/Page404';
import DashboardAppPage from './pages/DashboardAppPage';
import DevicePage from './pages/DevicePage';
import SettingsPage from './pages/SettingsPage';
import ProjectSettingsPage from './pages/project/ProjectSettingsPage';

// project pages
import StratificationPage from './pages/project/StratificationPage';
import CapturePage from './pages/project/CapturePage';
import SiteVisitPage from './pages/project/SiteVisitPage';
import SamplingPage from './pages/project/SamplingPage';
import ProjectHomePage from './pages/project/ProjectHomePage';
import FeasibilityEligibilityPage from './pages/project/FeasibilityEligibilityPage';
import FeasibilityPage from './pages/project/FeasibilityPage';
import BaselinePage from './pages/project/BaselinePage';
import MonitoringPage from './pages/project/MonitoringPage';
import ProjectWorkItemPage from './pages/project/ProjectWorkItemPage';
import LibraryPage from './pages/project/LibraryPage';
import FieldRecordPage from './pages/project/FieldRecordPage';
import ChangeLogPage from './pages/project/ChangeLogPage';
import PublicMapPage from './pages/PublicMapPage';
import SOPPage from './pages/project/SOPPage';
import EvidenceReviewPage from './pages/project/EvidenceReviewPage';
import KKLDemoPage from './pages/project/KKLDemoPage';

// measure pages
import MeasureHomePage from './pages/measure/MeasureHomePage';
import MeasureWizardPage from './pages/measure/MeasureWizardPage';
import MeasureStubPage from './pages/measure/MeasureStubPage';
import MeasureBaselinePage from './pages/measure/MeasureBaselinePage';
import MeasureMonitoringPage from './pages/measure/MeasureMonitoringPage';
import MeasureSpatialPage from './pages/measure/MeasureSpatialPage';
import MeasureCalibrationsPage from './pages/measure/MeasureCalibrationsPage';
import MeasureFieldDataPage from './pages/measure/MeasureFieldDataPage';
import MeasureSettingsPage from './pages/measure/MeasureSettingsPage';
import SitesRegistryPage from './pages/project/registry/SitesRegistryPage';
import StakeholdersPage from './pages/project/registry/StakeholdersPage';
import GrievancesPage from './pages/project/registry/GrievancesPage';

// ----------------------------------------------------------------------

export default function Router() {
  const routes = useRoutes([
    {
      path: '/admin',
      element: <AuthGuard dimension="iot"><AdminLayout /></AuthGuard>,
      children: [
        { element: <Navigate to="/admin/dashboard" />, index: true },
        { path: 'dashboard', element: <DashboardAppPage /> },
        { path: 'device/:wfmc', element: <DevicePage /> },
        { path: 'system', element: <SystemStatusPage /> },
        { path: 'fleet', element: <FleetManagementPage /> },
        { path: 'devices', element: <DevicesPage /> },
        { path: 'reads', element: <ReadsPage /> },
        { path: 'settings', element: <SettingsPage /> },
        { path: 'access', element: <Navigate to="/admin/settings" replace /> },
        { path: 'project-setup', element: <Navigate to="/project/settings" replace /> },
      ],
    },
    {
      path: '/project',
      element: <AuthGuard dimension="project"><ProjectLayout /></AuthGuard>,
      children: [
        { element: <Navigate to="/project/home" />, index: true },
        { path: 'home', element: <ProjectHomePage /> },
        { path: 'feasibility', element: <FeasibilityPage /> },
        { path: 'feasibility/eligibility', element: <FeasibilityEligibilityPage /> },
        { path: 'baseline', element: <BaselinePage /> },
        { path: 'monitoring', element: <MonitoringPage /> },
        { path: 'change-log', element: <ChangeLogPage /> },
        { path: 'sop', element: <SOPPage /> },
        { path: 'registry/sites', element: <SitesRegistryPage /> },
        { path: 'registry/stakeholders', element: <StakeholdersPage /> },
        { path: 'registry/grievances', element: <GrievancesPage /> },
        { path: 'work/:type', element: <ProjectWorkItemPage /> },
        { path: 'stratification', element: <StratificationPage /> },
        { path: 'library', element: <LibraryPage /> },
        { path: 'geometry-review', element: <Navigate to="/project/settings?tab=geometry" replace /> },
        { path: 'capture', element: <CapturePage /> },
        { path: 'evidence-review', element: <EvidenceReviewPage /> },
        { path: 'site-visit', element: <SiteVisitPage /> },
        { path: 'sampling', element: <SamplingPage /> },
        { path: 'field-record/:id', element: <FieldRecordPage /> },
        { path: 'kkl-demo', element: <KKLDemoPage /> },
        { path: 'settings', element: <ProjectSettingsPage /> },
      ],
    },
    {
      // PUBLIC map (no AuthGuard) — the field tool's review surface; all data it
      // shows comes from deliberately token-less read endpoints.
      path: '/fieldmap',
      element: <PublicMapPage />,
    },
    {
      path: '/measure',
      element: <AuthGuard dimension="project"><MeasureLayout /></AuthGuard>,
      children: [
        { element: <Navigate to="/measure/dashboard" />, index: true },
        { path: 'dashboard', element: <MeasureHomePage /> },
        { path: 'baseline', element: <MeasureBaselinePage /> },
        { path: 'field-data', element: <MeasureFieldDataPage /> },
        { path: 'monitoring', element: <Navigate to="/measure/field-data" replace /> },
        { path: 'spatial', element: <MeasureSpatialPage /> },
        { path: 'calibrations', element: <MeasureCalibrationsPage /> },
        { path: 'reports', element: <MeasureStubPage title="Reports" body="Annual sequestration totals, model comparison, exports for VERRA and UNFCCC reporting." /> },
        { path: 'settings', element: <MeasureSettingsPage /> },
        { path: 'demo', element: <KKLDemoPage /> },
      ],
    },
    // Back-compat: old /kkl/* URLs redirect to /measure/*
    { path: '/kkl', element: <Navigate to="/measure/dashboard" replace /> },
    { path: '/kkl/*', element: <Navigate to="/measure/dashboard" replace /> },
    {
      path: '/',
      element: <Navigate to="/login" replace />,
    },
    {
      path: 'login',
      element: <LoginPage />,
    },
    {
      path: 'waiting',
      element: <WaitingPage />,
    },
    {
      element: <SimpleLayout />,
      children: [
        { element: <Navigate to="/project/home" />, index: true },
        { path: '404', element: <Page404 /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return routes;
}
